export type Guid = string;
export type UserRole = 'Admin' | 'User';
export type UserStatus = 'PendingSetup' | 'Active' | 'Inactive' | 'Suspended';

export interface PagedResult<TItem> {
  items: TItem[];
  page: number;
  pageSize: number;
  total: number;
}

export interface UserListQuery {
  page: number;
  pageSize: number;
  search?: string;
  status?: UserStatus;
  role?: string;
  brandingId?: Guid;
}

export interface UserSummary {
  id: Guid;
  firstName: string;
  lastName: string;
  email: string;
  roles: UserRole[];
  status: UserStatus;
  brandingId: Guid;
  lastLoginDate?: string | null;
  createdDate: string;
  isLocked: boolean;
  failedLoginAttempts: number;
  // subscriptionCount: number;
  tierName?: string | null;
}

export interface UserDetails extends UserSummary {
  updatedDate?: string | null;
  subscriptionId?: Guid | null;
  passwordLastChangedDate?: string | null;
  passwordExpirationDate?: string | null;
  mustChangePassword?: boolean;
  subscriptions?: UserSubscription[];
  addOnNames?: string[];
}

export interface UserSubscription {
  id: Guid;
  tierId: number;
  startDate: string;
  endDate?: string | null;
  status: string;
}

export interface UserAuditRecord {
  id: Guid;
  timestamp: string;
  action: string;
  actorName: string;
  actorId?: Guid;
  details?: string | null;
}

export interface AdminCreateUserRequest {
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  brandingId: string;
  roles?: string[] | null;
  tierId?: number | null;
  addOnIds?: number[] | null;
}

export interface AdminCreateUserResponse {
  userId: string;
  invitationSent: boolean;
  subscriptionId?: string | null;
  pricing?: SubscriptionPricingDto | null;
}

export interface AdminUpdateUserRequest {
  firstName?: string | null;
  lastName?: string | null;
  tierId?: number | null;
  addOnIds?: number[] | null;
}

export interface UserStatusUpdateRequest {
  newStatus: UserStatus;
  reason?: string;
}

export interface AssignTierRequest {
  userId: string;
  tierId: number;
  startDate?: string | null;
}

export interface AssignAddOnsRequest {
  addOnIds?: number[] | null;
}

export interface TierDto {
  id: number;
  name: string;
  description?: string | null;
  monthlyPrice: number;
  annualPrice: number;
  isPurchasableSeparately: boolean;
  isActive: boolean;
  displayOrder: number;
  createdDate: string;
  updatedDate: string;
}

export interface CreateTierDto {
  name: string;
  description?: string | null;
  monthlyPrice: number;
  annualPrice: number;
  isActive?: boolean;
  displayOrder: number;
}

export interface UpdateTierDto {
  name: string;
  description?: string | null;
  monthlyPrice: number;
  annualPrice: number;
  isActive: boolean;
  displayOrder: number;
}

export enum PlatformAddOn {
  Website = 1,
  JobBoard = 2,
  BillingAndProposals = 3,
  BookingAndScheduling = 4,
  Forms = 5,
}

export interface AddOnDto {
  id: number;
  code: PlatformAddOn;
  name: string;
  description: string | null;
  monthlyPrice: number;
  annualPrice: number;
  isActive: boolean;
  isPurchasableSeparately: boolean;
  displayOrder: number;
}

export interface CreateAddOnDto {
  code: PlatformAddOn;
  name: string;
  description: string | null;
  monthlyPrice: number;
  annualPrice: number;
  isActive: boolean;
  isPurchasableSeparately: boolean;
  displayOrder: number;
}

export interface UpdateAddOnDto {
  name: string;
  code: PlatformAddOn;
  description: string | null;
  monthlyPrice: number;
  annualPrice: number;
  isActive: boolean;
  isPurchasableSeparately: boolean;
  displayOrder: number;
}
export interface TierAddOnDto {
  tierId: number;
  addOnId: number;
  isIncluded: boolean;
}

export interface UserSubscriptionAddOnDto {
  id: string;
  userSubscriptionId: string;
  addOnId: number;
  isActive: boolean;
  assignedDate: string;
}

export interface UserSubscriptionDetailsDto {
  id: string;
  userId: string;
  tier: TierDto;
  includedAddOns: AddOnDto[];
  purchasedAddOns: AddOnDto[];
  startDate: string;
  endDate?: string | null;
  status: string;
}

export interface SubscriptionPricingDto {
  tierMonthlyPrice: number;
  tierAnnualPrice: number;
  addOnsMonthlyTotal: number;
  addOnsAnnualTotal: number;
  grandMonthlyTotal: number;
  grandAnnualTotal: number;
}